/**
 * ConversationStarters — Suggested questions shown above the input on a new chat.
 *
 * Single row of neutral pills, configured per assistant. Clicking one fills the input (it does not
 * send) so the user can edit before submitting. Renders nothing when there is nothing to show, so
 * an assistant without starters keeps the input exactly as it is today.
 *
 * The row never scrolls: pills that do not fit the available width collapse into an overflow button
 * that reveals them on hover. Widths are measured at runtime because both the starter texts and the
 * container width vary (900px standalone vs ~376px embedded).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";

const GAP_PX = 6; // gap-1.5 between pills
const OVERFLOW_BUTTON_PX = 30; // matches the 30px icon buttons in the input toolbar
const CLOSE_DELAY_MS = 150; // lets the pointer cross the gap between button and panel

// Both the row and the overflow panel render the same object, so they share one look.
const PILL_BASE =
  "text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 " +
  "hover:bg-gray-50 hover:text-gray-900 transition-colors " +
  "disabled:opacity-50 disabled:cursor-default";

// In the row a pill must stay on one line, so long text is truncated (full text in `title`).
const ROW_PILL_CLASS =
  `inline-flex items-center shrink-0 whitespace-nowrap max-w-[260px] px-3 py-1.5 ${PILL_BASE}`;

// In the panel there is vertical room, and revealing the full text is the whole point of it.
// The panel itself draws nothing: these read as the same pills, just stacked.
const MENU_PILL_CLASS = `text-left max-w-full px-3 py-2 leading-snug ${PILL_BASE}`;

/**
 * How many pills fit in `available` px, reserving room for the overflow button when needed.
 * Layout is [pill] gap [pill] ... gap [button].
 */
export const fitPills = (widths, available) => {
  if (!widths.length) return 0;
  if (available <= 0) return widths.length;

  const totalAll = widths.reduce((sum, w) => sum + w, 0) + GAP_PX * (widths.length - 1);
  if (totalAll <= available) return widths.length;

  let used = OVERFLOW_BUTTON_PX;
  let count = 0;
  for (const width of widths) {
    const next = used + width + GAP_PX;
    if (next > available) break;
    used = next;
    count += 1;
  }
  return count;
};

const ConversationStarters = ({ starters, visible, disabled = false, onSelect }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const widthsRef = useRef(null);
  const closeTimerRef = useRef(null);
  // null while measuring: every pill is rendered so its natural width can be read.
  const [visibleCount, setVisibleCount] = useState(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const items = Array.isArray(starters) ? starters : [];
  const key = items.join(" ");

  // Re-measure from scratch whenever the starters themselves change (e.g. switching agent).
  useEffect(() => {
    widthsRef.current = null;
    setVisibleCount(null);
    setIsOverflowOpen(false);
  }, [key]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    setAvailableWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      setAvailableWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [key]);

  // Runs before paint so the overflowing row is never shown to the user.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (widthsRef.current === null) {
      const pills = el.querySelectorAll("[data-starter-pill]");
      if (pills.length !== items.length) return;
      widthsRef.current = Array.from(pills, (pill) => pill.offsetWidth);
    }
    const width = availableWidth || el.clientWidth;
    setVisibleCount(fitPills(widthsRef.current, width));
  }, [key, availableWidth, items.length]);

  const openOverflow = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOverflowOpen(true);
  }, []);

  const scheduleCloseOverflow = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsOverflowOpen(false), CLOSE_DELAY_MS);
  }, []);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Close on outside click or Escape, same as PlusMenu.
  useEffect(() => {
    if (!isOverflowOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOverflowOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOverflowOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOverflowOpen]);

  const handleSelect = useCallback(
    (text) => {
      setIsOverflowOpen(false);
      onSelect(text);
    },
    [onSelect],
  );

  if (!visible || items.length === 0) return null;

  const shown = visibleCount === null ? items : items.slice(0, visibleCount);
  const hidden = visibleCount === null ? [] : items.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={t("conversationStarters")}
      className="flex items-center gap-1.5 mb-1.5"
    >
      {/* Only the pills are clipped: overflow-hidden here would also clip the popover, which is
          positioned above the row and therefore entirely outside this box. */}
      <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
        {shown.map((text, index) => (
          <button
            key={`${index}-${text}`}
            type="button"
            data-starter-pill
            title={text}
            disabled={disabled}
            onClick={() => handleSelect(text)}
            className={ROW_PILL_CLASS}
          >
            <span className="min-w-0 truncate">{text}</span>
          </button>
        ))}
      </div>

      {hidden.length > 0 && (
        <div
          className="relative shrink-0"
          onMouseEnter={openOverflow}
          onMouseLeave={scheduleCloseOverflow}
        >
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsOverflowOpen((prev) => !prev)}
            onFocus={openOverflow}
            aria-label={t("conversationStartersMore", { count: hidden.length })}
            aria-haspopup="true"
            aria-expanded={isOverflowOpen}
            className={`w-[30px] h-[30px] rounded-lg flex items-center justify-center border-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default ${
              isOverflowOpen
                ? "bg-gray-200 text-gray-900"
                : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            <MoreHorizontal size={16} />
          </button>

          {/* Always rendered, toggled via opacity — same idiom as PlusMenu. */}
          <div
            role="menu"
            // Absolutely positioned inside the 30px button wrapper, so without an explicit width
            // shrink-to-fit would collapse the panel to its longest word. `w-max` keeps each pill
            // on one line, and the row's own width caps it so it never spills out of the chat.
            style={{ maxWidth: availableWidth || undefined }}
            className={`absolute bottom-full right-0 mb-2 flex flex-col items-end gap-1.5 w-max z-50 transition-all duration-150 ${
              isOverflowOpen
                ? "opacity-100 pointer-events-auto translate-y-0"
                : "opacity-0 pointer-events-none translate-y-1"
            }`}
          >
            {hidden.map((text, index) => (
              <button
                key={`${index}-${text}`}
                type="button"
                role="menuitem"
                disabled={disabled}
                onClick={() => handleSelect(text)}
                className={MENU_PILL_CLASS}
              >
                {text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { ConversationStarters };
export default ConversationStarters;
